"use client";
import {m} from "framer-motion";
import Image from "next/image";
import {frontendSkills, backendSkills, tools} from "@/data/skills";
import AnimatedSection from "@/components/ui/AnimatedSection";
import {type SectionProps} from "@/types/types";

const categories = [
  {title: "Frontend Modules", skills: frontendSkills},
  {title: "Backend Modules", skills: backendSkills},
  {title: "Tools & Environment", skills: tools},
];

export default function SkillsSection({delay = 0}: SectionProps) {
  return (
    <AnimatedSection delay={delay} id="skills" className="py-24 border-t-2 border-zinc-800 font-mono">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <m.div
          initial={{opacity: 0, y: 20}}
          whileInView={{opacity: 1, y: 0}}
          viewport={{once: true}}
          transition={{duration: 0.4}}
          className="mb-14"
        >
          <h2 className="text-4xl md:text-5xl font-black uppercase text-zinc-50 tracking-tighter">
            SKILLS_INVENTORY
          </h2>
          <div className="w-16 h-1.5 bg-yellow-400 mt-3" />
          <p className="mt-4 text-zinc-400 text-sm max-w-lg">
            Index of libraries, runtimes, and engineering environments loaded in core memory.
          </p>
        </m.div>

        {categories.map((category) => (
          <div key={category.title} className="mb-14 last:mb-0">
            <h3 className="text-xs font-black text-yellow-400 uppercase tracking-widest mb-6">
              [CATEGORY: {category.title.toUpperCase()}]
            </h3>

            <m.div
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              initial="hidden"
              whileInView="visible"
              viewport={{once: true, amount: 0.1}}
              variants={{
                hidden: {},
                visible: {transition: {staggerChildren: 0.02}},
              }}
            >
              {category.skills.map((skill) => (
                <m.div
                  key={skill.id}
                  variants={{
                    hidden: {opacity: 0, y: 10},
                    visible: {opacity: 1, y: 0},
                  }}
                  className="group relative p-4 border-2 border-zinc-800 bg-zinc-900/40 hover:border-yellow-400 transition-none select-none rounded-none"
                >
                  <div className="flex items-center gap-3">
                    <div className="relative shrink-0 w-8 h-8 border border-zinc-800 bg-zinc-950 p-1 flex items-center justify-center">
                      <Image
                        src={skill.image}
                        alt={skill.name}
                        width={24}
                        height={24}
                        className="grayscale group-hover:grayscale-0 transition-all duration-300"
                      />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-bold text-xs text-zinc-100 uppercase tracking-tight truncate">
                        {skill.name}
                      </h4>
                      <p className="text-[10px] text-zinc-500 truncate mt-0.5">
                        {skill.description}
                      </p>
                    </div>
                  </div>
                </m.div>
              ))}
            </m.div>
          </div>
        ))}
      </div>
    </AnimatedSection>
  );
}
